import React, { useEffect, useRef } from 'react';
import Intercom, { shutdown, update } from '@intercom/messenger-js-sdk';
import moment from 'moment';

/**
 * Safely sends an event to Intercom.
 *
 * @param {string} event_name - The name of the Intercom event (must be string).
 * @param {Object} metadata - Optional metadata object to send with the event.
 */
export const IntercomSendEvents = (event_name, metadata = {}) => {
  if (
    typeof window === 'undefined' ||
    typeof Intercom !== 'function' ||
    typeof event_name !== 'string' ||
    !event_name.trim()
  ) {
    console.warn('⚠️ Intercom event not sent: invalid environment or event name.', { event_name });
    return;
  }

  try {
    Intercom('trackEvent', event_name, metadata);
  } catch (err) {
    console.error(`❌ Failed to send Intercom event: ${event_name}`, err);
  }
};

/**
 * IntercomComponent - Initializes Intercom using the Messenger SDK for valid users.
 * Only runs once when the component is mounted and user is valid.
 */
export default function IntercomComponent({ user, nonLoggedInUserEmail }) {
  if (typeof window == 'undefined') return <></>;

  const app_id = process.env.REACT_APP_INTERCOM_APP_ID;
  if (!app_id) return <></>;

  // Refs to remember the last initialized user ID or visitor email
  const prevUserIdRef = useRef(null);
  const prevEmailRef = useRef(null);

  // Destructure user info
  const { id, attributes } = user || {};
  const { email, createdAt, deleted, banned, profile = {} } = attributes || {};

  const { firstName, lastName } = profile;

  const userId = id?.uuid;
  const userName = `${firstName || ''} ${lastName || ''}`.trim();
  const isValidUser = !!userId && !!email && !deleted && !banned;

  useEffect(() => {
    // CASE 1: Logged-in user is present
    if (isValidUser) {
      // If this is a different user than before, restart Intercom
      if (prevUserIdRef.current !== userId) {
        // If any previous Intercom session exists (user or email), shut it down first
        if (prevUserIdRef.current || prevEmailRef.current) {
          shutdown(); // Clear any existing Intercom session
        }
        // Boot Intercom with the new user's details
        Intercom({
          app_id,
          user_id: userId,
          name: userName,
          email: email,
          created_at: moment(createdAt).unix(),
        });
        // Update refs to record the current session
        prevUserIdRef.current = userId;
        prevEmailRef.current = null;
      } else {
        // Same user ID as before: update any changed fields (e.g. name or email)
        update({
          name: userName,
          email: email,
        });
      }
    }

    // CASE 2: No logged-in user, but we have a visitor email
    else if (!user && nonLoggedInUserEmail) {
      // If this is a new visitor email, restart Intercom session as a visitor
      if (prevEmailRef.current !== nonLoggedInUserEmail) {
        if (prevUserIdRef.current || prevEmailRef.current) {
          shutdown(); // Clear previous session
        }
        Intercom({
          app_id: app_id,
          email: nonLoggedInUserEmail,
        });
        prevEmailRef.current = nonLoggedInUserEmail;
        prevUserIdRef.current = null;
      }
      // No update() needed here since visitor session typically has only email
    }

    // CASE 3: Neither user nor visitor email – ensure Intercom is shut down
    else {
      if (prevUserIdRef.current || prevEmailRef.current) {
        shutdown();
        prevUserIdRef.current = null;
        prevEmailRef.current = null;
      }
    }
  }, [
    userId, // run effect when user ID changes
    userName, // or when user’s name changes (to update)
    email, // or when user’s email changes (to update)
    nonLoggedInUserEmail, // or when fallback email changes
  ]);

  return <></>;
}
